import React, { useEffect, useRef, useMemo, useState, useCallback } from 'react';
import { createPluginUI } from 'molstar/lib/mol-plugin-ui';
import { ColorNames } from 'molstar/lib/mol-util/color/names';
import 'molstar/lib/mol-plugin-ui/skin/light.scss';
import { renderReact18 } from 'molstar/lib/mol-plugin-ui/react18';
import { PluginCommands } from 'molstar/lib/mol-plugin/commands';
import { Script } from 'molstar/lib/mol-script/script';
import { StructureSelection } from 'molstar/lib/mol-model/structure/query';
import { MolScriptBuilder as MS, MolScriptBuilder } from 'molstar/lib/mol-script/language/builder';
import { StateTransforms } from 'molstar/lib/mol-plugin-state/transforms';
import { createStructureRepresentationParams } from 'molstar/lib/mol-plugin-state/helpers/structure-representation-params';
import { Color } from "molstar/lib/mol-util/color";
import { StructureElement, StructureProperties, Unit } from 'molstar/lib/mol-model/structure';
import { CustomInteractions, InteractionsShape } from 'molstar/lib/extensions/interactions/transforms';
import { ShapeRepresentation3D } from 'molstar/lib/mol-plugin-state/transforms/representation';
import { StructureFocusRepresentation } from 'molstar/lib/mol-plugin/behavior/dynamic/selection/structure-focus-representation';
import { getCif } from '../../ApiFunctions';
import { alignAndSuperposeWithSIFTSMapping } from 'molstar/lib/mol-model/structure/structure/util/superposition-sifts-mapping';
import { Mat4 } from 'molstar/lib/mol-math/linear-algebra';
import { StateObjectRef } from 'molstar/lib/mol-state';
import { setSubtreeVisibility } from 'molstar/lib/mol-plugin/behavior/static/state';
import multimerData from '../../mapa_alphafold.json';


// ──────────────────────────────────────────────────────────────────────────
// Contact extras (intra/inter tabs): the full interaction network as dashed
// lines, and one label per contact residue. Both are built once per load and
// switched with `setSubtreeVisibility`, so the buttons never rebuild the scene.
// ──────────────────────────────────────────────────────────────────────────
const NETWORK_COLOR = 0x9AA5B1;     // muted grey-blue: the selected contact (khaki) stays readable
const NETWORK_RADIUS = 0.05;        // thinner than the selected contact (0.11)
const NETWORK_CARBON = 0x8C96A0;    // carbons of the network sticks; heteroatoms keep CPK
const CONTACT_LABEL_COLOR = 0x111111;

const toSeqId = (p) => {
    if (typeof p === 'number') return p;
    const m = String(p ?? '').match(/(\d+)\s*$/);
    return m ? parseInt(m[1], 10) : parseInt(String(p ?? ''), 10);
};

// Rows come straight from the contacts tables, which encode the pair in
// `details` as "CHAIN/POS/ATOM | CHAIN/POS/ATOM" — the same string the row-click
// handlers already parse. Explicit field names are accepted as a fallback, so a
// caller with a different row shape only has to touch this function.
function toNetworkPair(row) {
    if (!row) return null;

    if (typeof row.details === 'string' && row.details.includes('|')) {
        const [left, right] = row.details.split('|').map(part => part.trim().split('/'));
        if (left?.length >= 2 && right?.length >= 2) {
            const aPos = toSeqId(left[1]);
            const bPos = toSeqId(right[1]);
            if (!Number.isNaN(aPos) && !Number.isNaN(bPos)) {
                return {
                    aChain: left[0], aPos, aAtom: left[2],
                    bChain: right[0], bPos, bAtom: right[2],
                    distance: row.distance !== undefined ? parseFloat(row.distance) : undefined,
                };
            }
        }
    }
    const aPos = toSeqId(row.aPos ?? row.position1 ?? row.pos1 ?? row.res1);
    const bPos = toSeqId(row.bPos ?? row.position2 ?? row.pos2 ?? row.res2);
    if (Number.isNaN(aPos) || Number.isNaN(bPos)) return null;
    return {
        aChain: row.aChain ?? row.chain1 ?? row.chainA,
        aPos,
        aAtom: row.aAtom ?? row.atom1 ?? row.residue1,
        bChain: row.bChain ?? row.chain2 ?? row.chainB,
        bPos,
        bAtom: row.bAtom ?? row.atom2 ?? row.residue2,
        distance: row.distance !== undefined ? parseFloat(row.distance) : undefined,
    };
}

// Anchor resolution for the interaction lines. An endpoint whose atom doesn't
// exist in the residue (CB on a glycine, CA on a ligand) resolves to an empty
// loci, and an empty loci's bounding-sphere centre is the origin — that's what
// draws a dash off into empty space. Indexing the atom names once lets each
// endpoint fall back to a real atom, and lets pairs whose residue isn't in this
// structure be dropped instead of drawn.
function buildResidueAtomIndex(structureData) {
    const index = new Map();
    if (!structureData) return index;
    for (const unit of structureData.units) {
        if (!Unit.isAtomic(unit) || unit.elements.length === 0) continue;
        const loc = StructureElement.Location.create(structureData, unit, unit.elements[0]);
        for (let i = 0, il = unit.elements.length; i < il; i++) {
            loc.element = unit.elements[i];
            const key = `${StructureProperties.chain.auth_asym_id(loc)}|${StructureProperties.residue.auth_seq_id(loc)}`;
            let atoms = index.get(key);
            if (!atoms) { atoms = new Set(); index.set(key, atoms); }
            atoms.add(StructureProperties.atom.label_atom_id(loc));
        }
    }
    return index;
}

// -> { atom } with the atom to anchor on (undefined = whole residue, i.e. its
// centroid: the sane fallback for ligands and for GLY without CB), or null when
// the residue isn't present in this structure at all.
function resolveAnchorAtom(index, chainId, seqId, preferred) {
    const atoms = index.get(`${chainId}|${seqId}`);
    if (!atoms) return null;
    for (const candidate of [preferred, 'CB', 'CA']) {
        if (candidate && atoms.has(candidate)) return { atom: candidate };
    }
    return { atom: undefined };
}

async function addContactNetwork(plugin, structureRef, structureData, pairs, forcedChain, pdbId) {
    if (!structureRef || !pairs?.length) return [];

    // In residue mode the table can hold rows from several structures; only the
    // ones belonging to the structure on screen can resolve to a loci.
    const sameStructure = (row) => !row?.identifier || !pdbId
        || String(row.identifier).toUpperCase() === String(pdbId).toUpperCase();

    const atomIndex = buildResidueAtomIndex(structureData);
    const interactions = [];
    const residues = new Map();   // `chain|seq` -> { chain, seq }, for the sticks
    let dropped = 0;

    for (const row of pairs) {
        if (!sameStructure(row)) continue;
        const p = toNetworkPair(row);
        if (!p) continue;

        const aChain = forcedChain || p.aChain;
        const bChain = forcedChain || p.bChain;
        if (!aChain || !bChain) continue;

        const anchorA = resolveAnchorAtom(atomIndex, aChain, p.aPos, p.aAtom);
        const anchorB = resolveAnchorAtom(atomIndex, bChain, p.bPos, p.bAtom);
        if (!anchorA || !anchorB) { dropped++; continue; }

        interactions.push({
            kind: 'unknown',
            aStructureRef: structureRef,
            a: { auth_asym_id: aChain, auth_seq_id: p.aPos, ...(anchorA.atom ? { label_atom_id: anchorA.atom } : {}) },
            bStructureRef: structureRef,
            b: { auth_asym_id: bChain, auth_seq_id: p.bPos, ...(anchorB.atom ? { label_atom_id: anchorB.atom } : {}) },
            description: `${aChain}/${p.aPos} \u2194 ${bChain}/${p.bPos}`
                + (Number.isFinite(p.distance) ? ` - ${p.distance.toFixed(2)}\u00c5` : ''),
        });
        residues.set(`${aChain}|${p.aPos}`, { chain: aChain, seq: p.aPos });
        residues.set(`${bChain}|${p.bPos}`, { chain: bChain, seq: p.bPos });
    }

    if (dropped > 0) {
        console.warn(`Contact network: ${dropped} pair(s) skipped, residue not in ${pdbId}`);
    }
    if (!interactions.length) return [];

    const refs = [];
    try {
        const update = plugin.build();
        const node = update.toRoot().apply(
            CustomInteractions,
            { interactions },
            { dependsOn: [structureRef], label: 'Contact network' }
        );
        // `kinds` is left at its default: the param was renamed across Mol*
        // versions and the default already covers 'unknown'.
        node.apply(InteractionsShape, {
            styles: { unknown: { color: Color(NETWORK_COLOR), style: 'dashed', radius: NETWORK_RADIUS } },
        }).apply(ShapeRepresentation3D);
        await update.commit();
        refs.push(node.ref);
    } catch (e) {
        console.warn('Contact network failed:', e);
        return refs;
    }

    // Sticks for the residues the network touches, so the lines land on visible
    // side chains instead of bare cartoon (same treatment as the path).
    try {
        const structCell = plugin.managers.structure.hierarchy.current.structures[0]?.cell;
        if (structCell && residues.size > 0) {
            const expressions = Array.from(residues.values()).map(({ chain, seq }) =>
                MolScriptBuilder.struct.generator.atomGroups({
                    'chain-test': MolScriptBuilder.core.rel.eq([
                        MolScriptBuilder.struct.atomProperty.macromolecular.auth_asym_id(), chain,
                    ]),
                    'residue-test': MolScriptBuilder.core.rel.eq([
                        MolScriptBuilder.struct.atomProperty.macromolecular.auth_seq_id(), seq,
                    ]),
                }));

            const sel = await plugin.builders.structure.tryCreateComponentFromExpression(
                structCell,
                MolScriptBuilder.struct.combinator.merge(expressions),
                'contact-network-residues',
                { label: 'Contact network residues' }
            );
            if (sel) {
                await plugin.builders.structure.representation.addRepresentation(sel, {
                    type: 'ball-and-stick',
                    typeParams: { sizeFactor: 0.16, aspectRatio: 1, ignoreHydrogens: true },
                    color: 'element-symbol',
                    colorParams: {
                        carbonColor: { name: 'uniform', params: { value: Color(NETWORK_CARBON) } },
                        saturation: 0,
                        lightness: 0,
                    },
                });
                refs.push(sel.ref);
            }
        }
    } catch (e) {
        console.warn('Contact network sticks failed:', e);
    }

    return refs;
}

async function addContactLabels(plugin, positions, chains) {
    if (!positions?.length || !chains?.length) return null;
    const structRef = plugin.managers.structure.hierarchy.current.structures[0]?.cell;
    if (!structRef) return null;

    // One atomGroup per (residue, chain) pair, merged: a position can never
    // bleed onto the wrong chain.
    const expressions = positions.map((pos, i) => {
        const seqId = toSeqId(pos);
        if (Number.isNaN(seqId) || !chains[i]) return null;
        return MolScriptBuilder.struct.generator.atomGroups({
            'chain-test': MolScriptBuilder.core.rel.eq([
                MolScriptBuilder.struct.atomProperty.macromolecular.auth_asym_id(), chains[i],
            ]),
            'residue-test': MolScriptBuilder.core.rel.eq([
                MolScriptBuilder.struct.atomProperty.macromolecular.auth_seq_id(), seqId,
            ]),
        });
    }).filter(Boolean);

    if (!expressions.length) return null;

    try {
        const combined = MolScriptBuilder.struct.combinator.merge(expressions);
        const sel = await plugin.builders.structure.tryCreateComponentFromExpression(
            structRef, combined, 'contact-labels', { label: 'Contact labels' }
        );
        if (!sel) return null;
        const repr = await plugin.builders.structure.representation.addRepresentation(sel, {
            type: 'label',
            typeParams: {
                level: 'residue',
                attachment: 'middle-left',
                sizeFactor: 1.1,
                background: false,
                borderWidth: 0.2,
                borderColor: Color(0xFFFFFF),
                tether: false,
                offsetZ: 1.5,
            },
            color: 'uniform',
            colorParams: { value: Color(CONTACT_LABEL_COLOR) },
        });
        return repr?.ref ?? null;
    } catch (e) {
        console.warn('Contact labels failed:', e);
        return null;
    }
}

// Mol*'s viewport buttons sit in a 32px column inset by 10px from the top right
// (`.msp-viewport-controls`), so these sit to their left. Colours/metrics are
// the light skin's `.msp-form-control`, hardcoded because the skin compiles to
// plain CSS scoped under `.msp-plugin`.
const MSP_CONTROL_SPACING = 10;
const MSP_CONTROL_COLUMN = 32;
const MSP_BUTTON_BAR_STYLE = {
    position: 'absolute',
    top: MSP_CONTROL_SPACING,
    right: MSP_CONTROL_SPACING + MSP_CONTROL_COLUMN + 6,
    zIndex: 10,
    display: 'flex',
    gap: 6,
};
const MSP_BUTTON_STYLE = {
    height: 32,
    padding: '0 10px',
    border: 'none',
    borderRadius: 0,
    background: '#F3F2EE',
    color: '#332B1F',
    fontFamily: '"Helvetica Neue", "Segoe UI", Helvetica, "Source Sans Pro", Arial, sans-serif',
    fontSize: 14,
    lineHeight: '30px',
    boxShadow: 'none',
    cursor: 'pointer',
};

const MolstarViewer = ({ pdbId, tab, positions, chain, modPositions, modChains, database, annotation, distance, residues, rowType, setLigandsOption, selectedLigand, uniac, superpose, contactPairs }) => {

const multimerMap = useMemo(() => {
  return multimerData.default || multimerData;
}, []);

  const viewerRef = useRef(null);
  const pluginInstanceRef = useRef(null);

  // Contact extras: state refs of the network and label subtrees, plus the
  // toggles. The *Ref mirrors let initMolstar honour the current toggle state
  // when the scene is rebuilt, without adding them to its dependency list.
  const networkRefStore = useRef([]);   // shape + sticks: both toggled together
  const labelsRefStore = useRef(null);
  const [showNetwork, setShowNetwork] = useState(false);
  const [showLabels, setShowLabels] = useState(true);
  const [hovered, setHovered] = useState(null);
  const showNetworkRef = useRef(false);
  const showLabelsRef = useRef(true);
  const hasContactTab = tab === 'intra-contacts' || tab === 'inter-contacts';

  const initMolstar = useCallback(async () => {
    // Dispose any existing plugin instance before creating a new one
    if (pluginInstanceRef.current) {
      pluginInstanceRef.current.dispose();
      pluginInstanceRef.current = null;
    }

    if (viewerRef.current) {
      const plugin = await createPluginUI({
        target: viewerRef.current,
        layoutIsExpanded: false,
        layoutShowControls: false,
        layoutShowRemoteState: false,
        layoutShowSequence: true,
        layoutShowLog: false,
        layoutShowLeftPanel: false,
        viewportShowExpand: false,
        viewportShowSelectionMode: false,
        viewportShowAnimation: false,
        render: renderReact18,
      });

      pluginInstanceRef.current = plugin;

      try {
        let data;
        // Target chain when the structure is a heterodimer from the map.
        let multimerChain;

    const hyphenCount = (pdbId.match(/-/g) || []).length;
    const isMultimer = /^AF-\d{16}$/.test(pdbId);
    if (hyphenCount >= 2 && !isMultimer) {
        database = "CO";
    }

    if (database === 'CO') {
        const cifString = await getCif(pdbId.replace("AF-", "").replace("_", "-"));
        data = await pluginInstanceRef.current.builders.data.rawData({
            data: cifString,
            label: `${pdbId}.cif`
        });

    } else if (isMultimer && uniac && multimerMap[uniac]) {
        // mapa_alphafold maps uniac -> [{ url, file, chain, partner, type }, ...].
        // Pick the record whose file matches this identifier (fall back to the
        // first). A heterodimer target sits on a single, maybe non-A, chain.
        const entry = multimerMap[uniac];
        let multimerUrl;
        if (typeof entry === 'string') {
            multimerUrl = entry; // legacy single-URL format
        } else {
            const stemOf = (f) => (f || '').replace(/-model_v\d+\.cif$/, '');
            const record = entry.find(r => stemOf(r.file) === pdbId) || entry[0];
            multimerUrl = record?.url;
            if (record?.type === 'heterodimer') multimerChain = record.chain;
        }
        data = await pluginInstanceRef.current.builders.data.download(
            { url: multimerUrl },
            { state: { isGhost: true } }
        );

    } else {
        const urlStructure = database === 'AF'
            ? `https://alphafold.ebi.ac.uk/files/AF-${pdbId.replace("AF-", "")}-F1-model_v6.cif`
            : `https://www.ebi.ac.uk/pdbe/entry-files/${pdbId.toLowerCase()}_updated.cif`;

        data = await pluginInstanceRef.current.builders.data.download(
            { url: urlStructure },
            { state: { isGhost: true } }
        );
    }

        // --- MANUAL STRUCTURE CONSTRUCTION (Prevents Color Race Conditions) ---

        // 1. Parse Trajectory
        const trajectory = await pluginInstanceRef.current.builders.structure.parseTrajectory(data,  'mmcif');
        const structure = await plugin.builders.structure.hierarchy.applyPreset(trajectory, 'default');

        // 2. Create Model and Structure Nodes manually
        const model = await pluginInstanceRef.current.builders.structure.createModel(trajectory);
        const structureNode = await pluginInstanceRef.current.builders.structure.createStructure(model);

        // 3. Configure Background
        const renderer = plugin.canvas3d.props.renderer;
        PluginCommands.Canvas3D.SetSettings(plugin, { settings: { renderer: { ...renderer, backgroundColor: ColorNames.white } } });
        plugin.state.updateBehavior(StructureFocusRepresentation, { expandRadius: 8 });

        // Get structure data for selections
         const dta = plugin.managers.structure.hierarchy.current.structures[0]?.cell.obj?.data;
         if (!dta) return;

        const cartoon = structure.representation.representations.polymer;

         // 5. Add polymer representation with WHITE color immediately
         const reprParamsStructureResetColor = createStructureRepresentationParams(plugin, undefined, {

                        color: 'uniform',
                        colorParams: { value: Color("0XFAFAFA") }
                    });
        const update = plugin.build();
        update.to(cartoon).update(reprParamsStructureResetColor);
        await update.commit();

        // --- TAB LOGIC ---

        if (tab === 'variants') {
            // Structure is already white (base). We only need to add the red spheres.

            if (chain?.length && positions?.length && cartoon) {
                const selection = Script.getStructureSelection(Q => Q.struct.generator.atomGroups({
                    "chain-test": Q.core.rel.eq([MolScriptBuilder.struct.atomProperty.macromolecular.auth_asym_id(), ...chain]),
                    'residue-test': Q.core.set.has([Q.set(...positions), Q.ammp('auth_seq_id')]),
                    'atom-test': MS.core.set.has([MS.set('CA'), MS.ammp('label_atom_id')]),
                    'group-by': Q.struct.atomProperty.macromolecular.residueKey(),
                }), dta);

                const reprParams = createStructureRepresentationParams(plugin, undefined, {
                    type: 'spacefill',
                    color: 'uniform',
                    colorParams: { value: Color(0xc93c20) },
                    size: 'physical',
                    sizeParams: { scale: 1 }
                });

                const update2 = plugin
                    .build()
                    .to(cartoon)
                    .apply(StateTransforms.Model.StructureSelectionFromExpression, {
                        label: 'Surroundings',
                        expression: selection
                    })
                    .apply(StateTransforms.Representation.StructureRepresentation3D, reprParams);

                    await update2.commit();
            }

        } else if (tab === 'intra-contacts'){
            // For a heterodimer loaded from the multimer map, the target
            // protein's intra-contacts all sit on one chain; prefer it.
            const intraChain = (idx) => (multimerChain || (chain && chain[idx]));
            // Structure is already white. Apply yellow highlights using Overpaint.

            const language = 'mol-script';
            const params = { layers: [] };

            if (positions?.length) {
              params.layers.push(...positions.map((pos, i) => ({
                script: Script(
                  `(sel.atom.atom-groups
                       :chain-test (= atom.auth_asym_id ${intraChain(i)} )
                       :residue-test (set.has (set ${pos}) atom.auth_seq_id)
                    )`,
                  language
                ),
                color: Color(0xf0e68c), // Yellow
                clear: false
              })));
            }

            if(cartoon){
                const update2 = plugin.build();
                update2
                      .to(cartoon)
                      .apply(StateTransforms.Representation.OverpaintStructureRepresentation3DFromScript, params);
                await update2.commit();
            }

            // Interaction lines
            if (distance && rowType === "main" && residues) {
                const update_interaction = plugin.build();
                const receptorRef = structureNode?.ref; // Use component reference

                if (receptorRef) {
                    const refs = [receptorRef];
                    const interactionsRef = update_interaction.toRoot().apply(CustomInteractions, {
                        interactions: [
                            {
                                kind: 'unknown',
                                aStructureRef: receptorRef,
                                a: { auth_seq_id: positions[0] , auth_asym_id: intraChain(0), label_atom_id: residues[0]},
                                bStructureRef: receptorRef,
                                b: { auth_seq_id:  positions[1] , auth_asym_id: intraChain(1), label_atom_id: residues[1]},
                                description: `${chain[0]}/${positions[0]}/${residues[0]}  ${chain[1]}/${positions[1]}/${residues[1]} - ${distance}Å`
                            }
                        ],
                    }, { dependsOn: refs, label: 'Interactions' });

                    interactionsRef.apply(InteractionsShape, {
                        kind: ['unknown'],
                        styles: { unknown: { color: Color(0xF0E68C), radius: 0.11 } }
                    }).apply(ShapeRepresentation3D);

                    PluginCommands.Toast.Show(plugin, {
                        title: 'Contact',
                        message: `${chain[0]}/${positions[0]}/${residues[0]}  ${chain[1]}/${positions[1]}/${residues[1]} - ${distance}Å`
                    });

                    await update_interaction.commit();
                }

                // Add Ball and Stick details
                 plugin.managers.lociLabels.addProvider({
                    label: (loci) => {
                        if (StructureElement.Loci.is(loci)) {
                            const loc = StructureElement.Loci.getFirstLocation(loci);
                            if (loc) {
                                const bFactor = StructureProperties.atom.B_iso_or_equiv(loc);
                                return `pLDDT: ${bFactor}`;
                            }
                        }
                        return undefined;
                    }
                });

                 const selection = Script.getStructureSelection(Q => Q.struct.generator.atomGroups({
                        "chain-test": Q.core.rel.eq([MolScriptBuilder.struct.atomProperty.macromolecular.auth_asym_id(), ...(multimerChain ? [multimerChain] : chain)]),
                        'residue-test': Q.core.set.has([Q.set(...positions), Q.ammp('auth_seq_id')])
                 }), dta);

                 const reprParams = createStructureRepresentationParams(plugin, undefined, {
                         type: 'ball-and-stick',
                         params: { sizeFactor: 0.2 } ,
                         color: 'element-symbol',
                         params: { carbonColor: { name: 'element-symbol' }},
                    });

                if(cartoon){
                    const updateBall = plugin
                        .build()
                        .to(cartoon)
                        .apply(StateTransforms.Model.StructureSelectionFromExpression, {
                            label: 'stick',
                            expression: selection
                        })
                        .apply(StateTransforms.Representation.StructureRepresentation3D, reprParams);
                    await updateBall.commit();
                }

                const loci = StructureSelection.toLociWithSourceUnits(selection);
                plugin.managers.camera.focusLoci(loci);
            }

        } else if (tab === 'inter-contacts'){

            const language = 'mol-script';
            const params = { layers: [] };

            if (positions?.length) {
              params.layers.push(...positions.map((pos, i) => ({
                script: Script(
                  `(sel.atom.atom-groups
                       :chain-test (= atom.auth_asym_id ${chain[i]} )
                       :residue-test (set.has (set ${pos}) atom.auth_seq_id)
                    )`,
                  language
                ),
                color: Color(0xf0e68c),
                clear: false
              })));
            }

            if (cartoon) {
                const update2 = plugin.build();
                update2
                      .to(cartoon)
                      .apply(StateTransforms.Representation.OverpaintStructureRepresentation3DFromScript, params);
                await update2.commit();
            }

            // Show line between contacts
            if (distance && rowType === "main" && residues) {
                const update_interaction = plugin.build();
                const receptorRef = structureNode?.ref;

                if (receptorRef) {
                    const refs = [receptorRef];
                    const interactionsRef = update_interaction.toRoot().apply(CustomInteractions, {
                        interactions: [
                            {
                                kind: 'unknown',
                                aStructureRef: receptorRef,
                                a: { auth_seq_id: positions[0] , auth_asym_id: chain[0], label_atom_id: residues[0]},
                                bStructureRef: receptorRef,
                                b: { auth_seq_id:  positions[1] , auth_asym_id: chain[1], label_atom_id: residues[1]},
                                description: `${chain[0]}/${positions[0]}/${residues[0]}  ${chain[1]}/${positions[1]}/${residues[1]} - ${distance.toFixed(2)}Å`
                            }
                        ],
                    }, { dependsOn: refs, label: 'Interactions' });

                    interactionsRef.apply(InteractionsShape, {
                        kind: ['unknown'],
                        styles: { unknown: { color: Color(0xF0E68C), radius: 0.11 } }
                    }).apply(ShapeRepresentation3D);

                    PluginCommands.Toast.Show(plugin, {
                        title: 'Contact',
                        message: `${chain[0]}/${positions[0]}/${residues[0]}  ${chain[1]}/${positions[1]}/${residues[1]} - ${distance.toFixed(2)}Å`
                    });

                    await update_interaction.commit();
                }

                const chainMap = new Map();
                for (let i = 0; i < chain.length; i++) {
                    const chainId = chain[i];
                    const pos = positions[i];
                    if (!chainMap.has(chainId)) {
                        chainMap.set(chainId, []);
                    }
                    chainMap.get(chainId).push(pos);
                }

                if (cartoon) {
                    for (const [chainId, chainPositions] of chainMap.entries()) {
                        const selection = Script.getStructureSelection(Q =>
                            Q.struct.generator.atomGroups({
                                "chain-test": Q.core.rel.eq([
                                    MolScriptBuilder.struct.atomProperty.macromolecular.auth_asym_id(),
                                    chainId
                                ]),
                                "residue-test": Q.core.set.has([
                                    Q.set(...chainPositions),
                                    Q.ammp('auth_seq_id')
                                ]),
                                "group-by": Q.struct.atomProperty.macromolecular.residueKey()
                            }), dta);

                        const reprParams = createStructureRepresentationParams(plugin, undefined, {
                                type: 'ball-and-stick',
                                params: { sizeFactor: 0.2 } ,
                                color: 'element-symbol',
                                params: { carbonColor: { name: 'element-symbol' }},
                        });

                        const updateBall = plugin
                            .build()
                            .to(cartoon)
                            .apply(StateTransforms.Model.StructureSelectionFromExpression, {
                                expression: selection
                            })
                            .apply(StateTransforms.Representation.StructureRepresentation3D, reprParams);

                        await updateBall.commit();
                        const loci = StructureSelection.toLociWithSourceUnits(selection);
                        plugin.managers.camera.focusLoci(loci);
                   }
                }
            }

        } else if (tab === 'ligands'){

            // Keep overpaint logic for ligands if needed
            const language = 'mol-script';
            const params = { layers: [] };
            if (positions?.length) {
                  params.layers.push(...positions.map((pos, i) => ({
                    script: Script(
                      `(sel.atom.atom-groups
                           :chain-test (= atom.auth_asym_id ${chain[i]} )
                           :residue-test (set.has (set ${pos}) atom.auth_seq_id)
                        )`,
                      language
                    ),
                    color: Color(0xf0e68c),
                    clear: false
                  })));
                }

            if(cartoon){
                const update2 = plugin.build();
                update2
                      .to(cartoon)
                      .apply(StateTransforms.Representation.OverpaintStructureRepresentation3DFromScript, params);
                await update2.commit();
            }

            // Render modified / PTM residues (SEP, TPO, PTR, ...) as ball-and-stick
            // so the modification is actually visible, not just white cartoon.
            if (cartoon && modPositions?.length && modChains?.length) {
                const modChainMap = new Map();
                for (let i = 0; i < modChains.length; i++) {
                    const chainId = modChains[i];
                    if (!modChainMap.has(chainId)) modChainMap.set(chainId, []);
                    modChainMap.get(chainId).push(modPositions[i]);
                }

                for (const [chainId, modPosList] of modChainMap.entries()) {
                    const modSelection = Script.getStructureSelection(Q =>
                        Q.struct.generator.atomGroups({
                            "chain-test": Q.core.rel.eq([
                                MolScriptBuilder.struct.atomProperty.macromolecular.auth_asym_id(),
                                chainId
                            ]),
                            "residue-test": Q.core.set.has([
                                Q.set(...modPosList),
                                Q.ammp('auth_seq_id')
                            ]),
                            "group-by": Q.struct.atomProperty.macromolecular.residueKey()
                        }), dta);

                    const modReprParams = createStructureRepresentationParams(plugin, undefined, {
                        type: 'ball-and-stick',
                        color: 'element-symbol',
                        colorParams: { carbonColor: { name: 'uniform', params: { value: Color(0xE066FF) } } },
                        size: 'physical',
                        sizeParams: { scale: 1 }
                    });

                    const updateMod = plugin
                        .build()
                        .to(cartoon)
                        .apply(StateTransforms.Model.StructureSelectionFromExpression, {
                            label: 'Modifications',
                            expression: modSelection
                        })
                        .apply(StateTransforms.Representation.StructureRepresentation3D, modReprParams);
                    await updateMod.commit();
                }
            }

            const mainStructureData = structureNode.cell.obj.data;

            const chemicalComponentMap = mainStructureData.model.properties.chemicalComponentMap;
            const aminoAcids = ["ALA", "ARG", "ASN", "ASP", "CYS", "GLN", "GLU", "GLY", "HIS", "ILE", "LEU", "LYS", "MET", "PHE", "PRO", "SER", "TRP", "THR", "TYR", "VAL", "HOH"];
            const detectedLigands = Array.from(chemicalComponentMap.keys()).filter(key => !aminoAcids.includes(key));
            setLigandsOption(prev => JSON.stringify(prev) !== JSON.stringify(detectedLigands) ? detectedLigands : prev);

            if (selectedLigand && selectedLigand.length > 0 && mainStructureData) {
                        const ligandSelectionScript = MS.struct.generator.atomGroups({
                            'residue-test': MS.core.set.has([MS.set(...selectedLigand), MS.ammp('label_comp_id')]),
                            'group-by': MS.struct.atomProperty.macromolecular.residueKey(),
                        });
                        const selection = Script.getStructureSelection(q => ligandSelectionScript, mainStructureData);
                        if (selection && selection.kind !== 'empty-loci') {
                            const loci = StructureSelection.toLociWithSourceUnits(selection);
                            plugin.managers.interactivity.lociSelects.selectOnly({ loci });
                            plugin.managers.structure.focus.setFromLoci(loci);
                    }
            }
        }

        // ── Contact extras (network + labels), built for both contact tabs ──
        if (tab === 'intra-contacts' || tab === 'inter-contacts') {
            // A heterodimer from the multimer map keeps the target on one chain.
            const forcedChain = tab === 'intra-contacts' ? multimerChain : undefined;
            const labelChains = (positions || []).map((_, i) => forcedChain || chain?.[i]);

            networkRefStore.current = await addContactNetwork(
                plugin, structureNode?.ref, dta, contactPairs, forcedChain, pdbId);
            for (const ref of networkRefStore.current) {
                setSubtreeVisibility(plugin.state.data, ref, !showNetworkRef.current);
            }

            labelsRefStore.current = await addContactLabels(plugin, positions, labelChains);
            if (labelsRefStore.current) {
                setSubtreeVisibility(plugin.state.data, labelsRefStore.current, !showLabelsRef.current);
            }
        }

      } catch (error) {
        console.error('Failed to load the PDB structure:', error);
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pdbId, tab, positions, chain, modPositions, modChains, database, annotation, distance, residues, selectedLigand, uniac, superpose, contactPairs]);

  useEffect(() => {
    initMolstar();

    // Cleanup only on unmount
    return () => {
      if (pluginInstanceRef.current) {
        pluginInstanceRef.current.dispose();
        pluginInstanceRef.current = null;
      }
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pdbId, tab, positions, chain, modPositions, modChains, database, annotation, distance, residues, selectedLigand, uniac, superpose, contactPairs]);

  // Toggles: hide/show the existing subtrees, no rebuild.
  useEffect(() => {
    showNetworkRef.current = showNetwork;
    const plugin = pluginInstanceRef.current;
    if (!plugin || !networkRefStore.current?.length) return;
    for (const ref of networkRefStore.current) {
        setSubtreeVisibility(plugin.state.data, ref, !showNetwork);
    }
  }, [showNetwork]);

  useEffect(() => {
    showLabelsRef.current = showLabels;
    const plugin = pluginInstanceRef.current;
    if (!plugin || !labelsRefStore.current) return;
    setSubtreeVisibility(plugin.state.data, labelsRefStore.current, !showLabels);
  }, [showLabels]);

  const buttonStyle = (id) => ({
    ...MSP_BUTTON_STYLE,
    outline: hovered === id ? '1px solid #C9C4B2' : 'none',
    outlineOffset: -1,
  });

  return (
    <div style={{ position: 'relative', width: '100%', height: '100%' }}>
      <div key={pdbId} ref={viewerRef} style={{ width: '100%', height: '100%' }} />
      {hasContactTab && (
        <div style={MSP_BUTTON_BAR_STYLE}>
          <button
            type="button"
            onClick={() => setShowNetwork(v => !v)}
            onMouseEnter={() => setHovered('network')}
            onMouseLeave={() => setHovered(null)}
            title="All contacts of this table as dashed lines"
            disabled={!contactPairs?.length}
            style={{
              ...buttonStyle('network'),
              opacity: contactPairs?.length ? 1 : 0.5,
              cursor: contactPairs?.length ? 'pointer' : 'default',
            }}
          >
            {showNetwork ? 'Hide network' : 'Show network'}
          </button>
          <button
            type="button"
            onClick={() => setShowLabels(v => !v)}
            onMouseEnter={() => setHovered('labels')}
            onMouseLeave={() => setHovered(null)}
            title="Residue labels on the contact residues"
            style={buttonStyle('labels')}
          >
            {showLabels ? 'Hide labels' : 'Show labels'}
          </button>
        </div>
      )}
    </div>
  );
};

export default MolstarViewer;